import { useEffect, useState, useCallback } from 'react';
import { applyIssueAction, listIssues, updateIssue, type Issue, type IssueCategory } from '@services/issues';
import { useAlertDialog } from '../../shared/AlertDialog';
import { getImageUrl } from '@utils/imageUrl';

type StatusFilter = 'all' | 'open' | 'resolved' | 'dismissed';
type CategoryFilter = 'all' | IssueCategory;

const CATEGORY_TABS: Array<{ value: CategoryFilter; label: string }> = [
	{ value: 'all', label: 'All categories' },
	{ value: 'garden_image', label: 'Garden photo reports' },
	{ value: 'offer', label: 'Offer reports' },
	{ value: 'plan', label: 'Plan reports' },
	{ value: 'project', label: 'Project reports' },
	{ value: 'user', label: 'User reports' }
];

const OFFER_REASON_LABELS: Record<string, string> = {
	misleading_details: 'Misleading details or pricing',
	suspected_scam: 'Suspected scam',
	inappropriate_content: 'Inappropriate content',
	spam_duplicate: 'Spam or duplicate offer',
	other: 'Other'
};

export default function AdminIssues() {
	const { showAlert } = useAlertDialog();
	const [issues, setIssues] = useState<Issue[]>([]);
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
	const [updatingId, setUpdatingId] = useState<number | null>(null);
	const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [offset, setOffset] = useState(0);
	const [total, setTotal] = useState(0);
	const limit = 15;

	function getAdminUserId(): number {
		try {
			const raw = window.sessionStorage.getItem('gardenhub:auth');
			if (!raw) return 0;
			const parsed = JSON.parse(raw) as { id?: string };
			return parsed?.id ? Number(parsed.id) : 0;
		} catch {
			return 0;
		}
	}

	useEffect(() => {
		setLoading(true);
		const status = statusFilter === 'all' ? undefined : statusFilter;
		const category = categoryFilter === 'all' ? undefined : categoryFilter;
		listIssues({ status, category, query: searchQuery.trim() || undefined, limit, offset })
			.then((payload) => {
				setIssues(payload.items);
				setTotal(payload.pagination?.total ?? payload.items.length);
			})
			.catch((err) => {
				console.error('Failed to load issues:', err);
				showAlert(err instanceof Error ? err.message : 'Failed to load issues.', { title: 'Error' });
				setIssues([]);
			})
			.finally(() => setLoading(false));
	}, [statusFilter, categoryFilter, searchQuery, offset, showAlert]);

	async function handleUpdateStatus(issue: Issue, newStatus: 'open' | 'resolved' | 'dismissed') {
		setUpdatingId(issue.id);
		try {
			const updated = await updateIssue(issue.id, { status: newStatus });
			setIssues((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
		} catch (err) {
			console.error('Failed to update issue:', err);
			showAlert(err instanceof Error ? err.message : 'Failed to update issue.', { title: 'Error' });
		} finally {
			setUpdatingId(null);
		}
	}

	async function handleSoftDeleteTarget(issue: Issue) {
		const adminUserId = getAdminUserId();
		if (!adminUserId) {
			showAlert('Could not detect current admin session.', { title: 'Action blocked' });
			return;
		}
		if (!window.confirm('Soft delete this reported target and resolve this issue?')) return;

		setUpdatingId(issue.id);
		try {
			const updated = await applyIssueAction(issue.id, {
				action: 'soft_delete_target',
				admin_user_id: adminUserId,
				reason_message: `Soft-deleted from admin issues panel (${issue.targetType ?? 'unknown'})`,
				issue_status: 'resolved'
			});
			setIssues((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
		} catch (err) {
			console.error('Failed to apply moderation action:', err);
			showAlert(err instanceof Error ? err.message : 'Failed to delete target.', { title: 'Error' });
		} finally {
			setUpdatingId(null);
		}
	}

	const openCount = issues.filter((i) => i.status === 'open').length;

	const closeLightbox = useCallback(() => setLightboxUrl(null), []);

	useEffect(() => {
		if (!lightboxUrl) return;
		const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeLightbox();
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [lightboxUrl, closeLightbox]);

	return (
		<>
			<h1 className="h3 mb-4">Issues</h1>
			<p className="text-muted mb-3">Review reports from users and mark each one as resolved or dismissed.</p>

			<div className="mb-3 d-flex flex-wrap align-items-center gap-2">
				<span className="text-muted small">Category:</span>
				{CATEGORY_TABS.map((category) => (
					<button
						key={category.value}
						type="button"
						className={`btn btn-sm ${categoryFilter === category.value ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => setCategoryFilter(category.value)}
					>
						{category.label}
					</button>
				))}
			</div>

			<div className="mb-3 d-flex flex-wrap align-items-center gap-2">
				<span className="text-muted small">Filter:</span>
				{(['all', 'open', 'resolved', 'dismissed'] as const).map((s) => (
					<button
						key={s}
						type="button"
						className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-outline-secondary'}`}
						onClick={() => setStatusFilter(s)}
					>
						{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
						{s === 'open' && openCount > 0 && (
							<span className="badge bg-light text-dark ms-1">{openCount}</span>
						)}
					</button>
				))}
			</div>
			<div className="mb-3 d-flex gap-2 align-items-center">
				<input
					type="search"
					className="form-control form-control-sm"
					placeholder="Search reporter, reason, target..."
					value={searchQuery}
					onChange={(e) => {
						setOffset(0);
						setSearchQuery(e.target.value);
					}}
					style={{ maxWidth: 360 }}
				/>
				{searchQuery && (
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => {
							setOffset(0);
							setSearchQuery('');
						}}
					>
						Clear
					</button>
				)}
				<span className="text-muted small ms-auto">Total: {total}</span>
			</div>

			{loading ? (
				<div className="text-muted">Loading…</div>
			) : issues.length === 0 ? (
				<div className="card">
					<div className="card-body text-center text-muted py-5">
						No issues match the current filter.
					</div>
				</div>
			) : (
				<div className="card">
					<ul className="list-group list-group-flush">
						{issues.map((issue) => (
							<li key={issue.id} className="list-group-item">
								<div className="d-flex flex-column flex-md-row gap-3">
									<div className="flex-shrink-0">
										{issue.imageUrl ? (
											<div
												className="rounded border bg-light d-flex align-items-center justify-content-center overflow-hidden"
												style={{ width: 120, height: 90, cursor: 'pointer' }}
												onClick={() => setLightboxUrl(getImageUrl(issue.imageUrl) ?? issue.imageUrl)}
												role="button"
												tabIndex={0}
												onKeyDown={(e) => e.key === 'Enter' && setLightboxUrl(getImageUrl(issue.imageUrl) ?? issue.imageUrl)}
												aria-label="View image larger"
											>
												<img
													src={getImageUrl(issue.imageUrl) ?? issue.imageUrl}
													alt="Reported evidence"
													className="rounded"
													style={{ width: 120, height: 90, objectFit: 'cover' }}
													onError={(e) => {
														const el = e.target as HTMLImageElement;
														el.style.display = 'none';
														const wrap = el.closest('.flex-shrink-0');
														const fallback = wrap?.querySelector('.issue-image-fallback');
														if (fallback) (fallback as HTMLElement).style.display = 'block';
													}}
												/>
												<div className="issue-image-fallback small text-muted text-center p-2" style={{ display: 'none', width: 120, height: 90 }}>
													Image unavailable
													<br />
													<a href={getImageUrl(issue.imageUrl) ?? issue.imageUrl} target="_blank" rel="noopener noreferrer" className="small" onClick={(e) => e.stopPropagation()}>Open link</a>
												</div>
											</div>
										) : (
											<div className="rounded border bg-light d-flex align-items-center justify-content-center text-muted small text-center p-2" style={{ width: 120, height: 90 }}>
												No image
											</div>
										)}
									</div>
									<div className="flex-grow-1 min-w-0">
										<div className="d-flex flex-wrap align-items-center gap-2 mb-1">
											<span className="badge bg-secondary">{issue.type}</span>
											{issue.category && (
												<span className="badge text-bg-light border">
													{issue.category === 'offer'
														? 'Offer'
														: issue.category === 'plan'
															? 'Plan'
															: issue.category === 'project'
																? 'Project'
																: issue.category === 'user'
																	? 'User'
																	: 'Garden photo'}
												</span>
											)}
											{typeof issue.targetReportCount === 'number' && issue.targetReportCount > 1 && (
												<span className="badge text-bg-danger">
													Reported {issue.targetReportCount}x
												</span>
											)}
											<span className={`badge ${issue.status === 'open' ? 'bg-warning' : issue.status === 'resolved' ? 'bg-success' : 'bg-secondary'}`}>
												{issue.status}
											</span>
											<span className="text-muted small">
												{new Date(issue.createdAt).toLocaleString()}
											</span>
										</div>
										<div className="small">
											<strong>Reporter:</strong>{' '}
											{issue.reporterName || issue.reporterEmail || `User #${issue.reporterUserId}`}
											{issue.reporterEmail && (
												<span className="text-muted"> ({issue.reporterEmail})</span>
											)}
										</div>
										{issue.category === 'offer' && (
											<div className="mt-2 small">
												<div>
													<strong>Target:</strong>{' '}
													{issue.offerId ? (
														<>
															Offer #{issue.offerId}
															{issue.offerPlanTitle ? ` - ${issue.offerPlanTitle}` : ''}
															{issue.offerPlanId ? ` (Plan #${issue.offerPlanId})` : ''}
														</>
													) : (
														issue.targetType && issue.targetId ? `${issue.targetType} #${issue.targetId}` : 'Offer'
													)}
												</div>
												{issue.offerGardenerName && (
													<div className="text-muted">
														Offer from: {issue.offerGardenerName}
													</div>
												)}
												{issue.reasonCode && (
													<div className="text-muted">
														Reason: {OFFER_REASON_LABELS[issue.reasonCode] ?? issue.reasonCode}
													</div>
												)}
											</div>
										)}
										{issue.category === 'project' && issue.projectName && (
											<div className="mt-2 small text-muted">
												<strong>Target project:</strong> {issue.projectName}
											</div>
										)}
										{issue.category === 'user' && (
											<div className="mt-2 small text-muted">
												<strong>Target user:</strong> {issue.targetUserName || (issue.targetUserId ? `User #${issue.targetUserId}` : 'Unknown')}
											</div>
										)}
										{issue.message && (
											<div className="mt-1 small text-muted">
												"{issue.message}"
											</div>
										)}
										{issue.adminNotes && (
											<div className="mt-1 small text-muted">
												<strong>Admin:</strong> {issue.adminNotes}
											</div>
										)}
										<div className="mt-2 d-flex flex-wrap gap-2">
											{issue.status !== 'open' && (
												<button
													type="button"
													className="btn btn-sm btn-outline-primary"
													disabled={updatingId === issue.id}
													onClick={() => handleUpdateStatus(issue, 'open')}
												>
													Reopen
												</button>
											)}
											{issue.status !== 'resolved' && (
												<button
													type="button"
													className="btn btn-sm btn-outline-success"
													disabled={updatingId === issue.id}
													onClick={() => handleUpdateStatus(issue, 'resolved')}
												>
													Resolve
												</button>
											)}
											{issue.status !== 'dismissed' && (
												<button
													type="button"
													className="btn btn-sm btn-outline-secondary"
													disabled={updatingId === issue.id}
													onClick={() => handleUpdateStatus(issue, 'dismissed')}
												>
													Dismiss
												</button>
											)}
											{issue.targetType && issue.targetId && (
												<button
													type="button"
													className="btn btn-sm btn-outline-danger"
													disabled={updatingId === issue.id}
													onClick={() => handleSoftDeleteTarget(issue)}
												>
													Soft delete target
												</button>
											)}
											{updatingId === issue.id && (
												<span className="spinner-border spinner-border-sm align-middle" role="status" aria-hidden />
											)}
										</div>
									</div>
								</div>
							</li>
						))}
					</ul>
				</div>
			)}
			<div className="mt-3 d-flex align-items-center justify-content-between">
				<button
					type="button"
					className="btn btn-sm btn-outline-secondary"
					disabled={offset === 0 || loading}
					onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
				>
					Previous
				</button>
				<span className="small text-muted">
					Showing {Math.min(offset + 1, total)}-{Math.min(offset + issues.length, total)} of {total}
				</span>
				<button
					type="button"
					className="btn btn-sm btn-outline-secondary"
					disabled={loading || offset + limit >= total}
					onClick={() => setOffset((prev) => prev + limit)}
				>
					Next
				</button>
			</div>

			{/* Lightbox: click thumbnail to view image larger */}
			{lightboxUrl && (
				<div
					className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
					style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1050 }}
					onClick={closeLightbox}
					role="button"
					tabIndex={0}
					aria-label="Close"
				>
					<button
						type="button"
						className="btn btn-light position-absolute top-0 end-0 m-3"
						onClick={closeLightbox}
						aria-label="Close"
					>
						<i className="bi bi-x-lg" />
					</button>
					<img
						src={lightboxUrl}
						alt="Reported garden (full size)"
						className="rounded shadow"
						style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain' }}
						onClick={(e) => e.stopPropagation()}
					/>
				</div>
			)}
		</>
	);
}
